import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import InfoHint from './InfoHint';
import StatCard from './StatCard';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { TableValue } from './ui/table-value';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Eye, Phone, FileSpreadsheet, FileDown, MapPin, List, ChevronRight, RefreshCw } from 'lucide-react';
import { format } from 'date-fns';
import {
  fetchComplaints,
  fetchComplaintsSummary,
  fetchComplaintsPoints,
  listComplaintsExportJobs,
  startComplaintsExport,
  downloadComplaintsExportJob,
  deleteComplaintsExportJob,
  startComplaintsPointsRefresh,
  listComplaintsPointsRefreshJobs,
  getComplaintsPointsRefreshJob
} from '../api';
import { PANEL_IDS } from '../config/panelRegistry';
import { usePanelSettings } from '../contexts/PanelSettingsContext';
import { ExportJobsProvider, useExportJobs } from './exports/ExportJobsContext';
import ExportJobsPanel from './exports/ExportJobsPanel';
import ComplaintCaseModal from './ComplaintCaseModal';
import TransferredPointWarning from './complaints/TransferredPointWarning';
import { toast } from '../hooks/use-toast';
import useLatestRequest from '../hooks/useLatestRequest';
import usePageForScope from '../hooks/usePageForScope';
import { useFeatures } from '../contexts/FeaturesContext';
import { STATUS_LABELS, STATUS_BADGE_CLASSES, STATUS_ORDER } from './complaints/status';
import {
  buildComplaintsQuery,
  caseColumnCount,
  caseFilterValue,
  visibleCaseColumns,
  visibleCaseFilters
} from './complaints/caseColumns';
import { formatNegativeCategory, formatNegativeSubCategory } from '../utils/negativeLabels';

const ACTIVE_JOB_STATUSES = new Set(['queued', 'running']);
const isActivePointsJob = (job) => ACTIVE_JOB_STATUSES.has(String(job?.status ?? '').toLowerCase());

const PAGE_SIZE = 20;

const complaintsExportApi = {
  listJobs: listComplaintsExportJobs,
  startExport: startComplaintsExport,
  downloadJob: downloadComplaintsExportJob,
  deleteJob: deleteComplaintsExportJob
};

const toDateParam = (value) => (value ? format(value, 'yyyy-MM-dd') : undefined);

const formatCaseDate = (value) => {
  if (!value) return '—';
  const parts = String(value).split('-');
  if (parts.length === 3) {
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ru-RU');
};

/** Ячейки строки кейса по id столбца. Состав и порядок столбцов — `complaints/caseColumns.js`. */
const CASE_CELLS = {
  [PANEL_IDS['ui:complaints.col-date']]: (item) => (
    <TableCell className="whitespace-nowrap">{formatCaseDate(item.complaintDate)}</TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-client']]: (item) => (
    <TableCell className="font-medium">{item.clientName || item.clientPhone}</TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-calls']]: (item) => (
    <TableCell numeric>
      <TableValue tone="info" icon={Phone}>{item.callsCount ?? 0}</TableValue>
    </TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-point']]: (item) => (
    <TableCell>
      {item.point ? (
        <span className="inline-flex items-center gap-1">
          {item.point}
          {item.pointTransferred && <TransferredPointWarning />}
        </span>
      ) : '—'}
    </TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-type']]: (item) => (
    <TableCell>{formatNegativeCategory(item.type)}</TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-detail']]: (item) => (
    <TableCell>{formatNegativeSubCategory(item.detail)}</TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-compensation']]: (item) => (
    <TableCell>{item.compensation || '—'}</TableCell>
  ),
  [PANEL_IDS['ui:complaints.col-status']]: (item) => (
    <TableCell>
      <Badge className={STATUS_BADGE_CLASSES[item.status] ?? STATUS_BADGE_CLASSES.new}>
        {STATUS_LABELS[item.status] ?? item.status}
      </Badge>
    </TableCell>
  )
};

const ComplaintsInner = ({ dateRange, callType = 'all' }) => {
  const panels = usePanelSettings();
  const { startExport, setIsPanelOpen } = useExportJobs();
  // Точка кейса берётся из заказа iiko, поэтому без интеграции всё «точечное» скрыто.
  const { features } = useFeatures();
  const iikoEnabled = features.iikoEnabled;

  const [view, setView] = useState('list');
  const [summary, setSummary] = useState(null);
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [points, setPoints] = useState([]);

  const [searchPhone, setSearchPhone] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [detailFilter, setDetailFilter] = useState('all');
  const [pointFilter, setPointFilter] = useState('all');
  const [onlyUnresolved, setOnlyUnresolved] = useState(false);
  const [hideNotComplaint, setHideNotComplaint] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  // Ответ на прежний период или фильтр, пришедший позже свежего, не должен его затирать.
  // Список и разрез по точкам делят один гейт: у них общие спиннер и ошибка, и смена режима
  // отменяет запрос прежнего.
  const summaryRequests = useLatestRequest();
  const listRequests = useLatestRequest();

  const dateParams = useMemo(
    () => ({ date_from: toDateParam(dateRange?.from), date_to: toDateParam(dateRange?.to) }),
    [dateRange?.from, dateRange?.to]
  );

  // Новый период или фильтр — с первой страницы. Раньше страницу сбрасывал эффект и только
  // по фильтрам: смена периода оставляла список на прежней странице, а сброс эффектом
  // сначала запрашивал старую страницу с новыми условиями.
  const pageScope = JSON.stringify([
    dateParams.date_from,
    dateParams.date_to,
    callType,
    searchPhone,
    statusFilter,
    typeFilter,
    detailFilter,
    pointFilter,
    onlyUnresolved,
    hideNotComplaint
  ]);
  const [page, setPage] = usePageForScope(pageScope);

  // Фильтры «Тип жалобы» и «Детализация» следуют за видимостью своих столбцов; скрытый
  // уходит в запрос как «все».
  const { type: showTypeFilter, detail: showDetailFilter } = visibleCaseFilters({ isVisible: panels.isVisible });
  const queryTypeFilter = caseFilterValue(typeFilter, showTypeFilter);
  const queryDetailFilter = caseFilterValue(detailFilter, showDetailFilter);

  const queryParams = useMemo(() => buildComplaintsQuery({
    dateParams,
    searchPhone,
    statusFilter,
    typeFilter: queryTypeFilter,
    detailFilter: queryDetailFilter,
    pointFilter,
    onlyUnresolved,
    hideNotComplaint,
    iikoEnabled,
    callType
  }), [
    dateParams, searchPhone, statusFilter, queryTypeFilter, queryDetailFilter, pointFilter,
    onlyUnresolved, hideNotComplaint, iikoEnabled, callType
  ]);

  const loadSummary = useCallback(async () => {
    const request = summaryRequests.begin();
    try {
      const data = await fetchComplaintsSummary(queryParams);
      if (request.isCurrent()) {
        setSummary(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      request.end();
    }
  }, [queryParams, summaryRequests]);

  const loadCases = useCallback(async () => {
    const request = listRequests.begin();
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchComplaints({
        ...queryParams,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE
      });
      if (request.isCurrent()) {
        setCases(response.items ?? []);
        setTotal(response.total ?? 0);
      }
    } catch (err) {
      console.error(err);
      if (request.isCurrent()) {
        setError('Не удалось загрузить список жалоб.');
      }
    } finally {
      const isCurrent = request.isCurrent();
      request.end();
      if (isCurrent) {
        setIsLoading(false);
      }
    }
  }, [queryParams, page, listRequests]);

  const loadPoints = useCallback(async () => {
    const request = listRequests.begin();
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchComplaintsPoints(queryParams);
      if (request.isCurrent()) {
        setPoints(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error(err);
      if (request.isCurrent()) {
        setError('Не удалось загрузить разрез по точкам.');
      }
    } finally {
      const isCurrent = request.isCurrent();
      request.end();
      if (isCurrent) {
        setIsLoading(false);
      }
    }
  }, [queryParams, listRequests]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Режим «по точкам» без iiko недоступен — если он остался в состоянии, возвращаемся к списку.
  const effectiveView = iikoEnabled ? view : 'list';

  useEffect(() => {
    if (effectiveView === 'list') {
      loadCases();
    } else {
      loadPoints();
    }
  }, [effectiveView, loadCases, loadPoints]);

  const typeOptions = useMemo(() => {
    const set = new Set();
    cases.forEach((c) => (c.categories ?? []).forEach((v) => v && set.add(v)));
    if (typeFilter !== 'all') set.add(typeFilter);
    return Array.from(set).sort();
  }, [cases, typeFilter]);

  const detailOptions = useMemo(() => {
    const set = new Set();
    cases.forEach((c) => (c.subCategories ?? []).forEach((v) => v && set.add(v)));
    if (detailFilter !== 'all') set.add(detailFilter);
    return Array.from(set).sort();
  }, [cases, detailFilter]);

  const handleExport = useCallback(() => {
    startExport(queryParams);
  }, [startExport, queryParams]);

  // ---- Фоновое обновление точек + поллинг прогресса ----
  const [pointsJob, setPointsJob] = useState(null);
  const pointsPollRef = useRef(null);
  const reloadRef = useRef(() => {});

  useEffect(() => {
    reloadRef.current = () => {
      loadSummary();
      if (effectiveView === 'list') loadCases(); else loadPoints();
    };
  }, [effectiveView, loadCases, loadPoints, loadSummary]);

  const pollPointsJob = useCallback((id) => {
    if (pointsPollRef.current) clearInterval(pointsPollRef.current);
    pointsPollRef.current = window.setInterval(async () => {
      try {
        const job = await getComplaintsPointsRefreshJob(id);
        setPointsJob(job);
        if (!isActivePointsJob(job)) {
          clearInterval(pointsPollRef.current);
          pointsPollRef.current = null;
          const status = String(job?.status ?? '').toLowerCase();
          if (status === 'completed') {
            toast({ title: 'Точки обновлены' });
          } else if (status === 'failed') {
            toast({ title: 'Не удалось обновить точки', description: job?.error, variant: 'destructive' });
          }
          reloadRef.current();
        }
      } catch (err) {
        console.error(err);
        clearInterval(pointsPollRef.current);
        pointsPollRef.current = null;
      }
    }, 2000);
  }, []);

  const handleRefreshPoints = useCallback(async () => {
    try {
      const response = await startComplaintsPointsRefresh(queryParams);
      const id = response?.jobId;
      if (id) {
        setPointsJob({ id, status: 'queued', processedRows: 0, totalRows: 0, progress: 0 });
        pollPointsJob(id);
      }
    } catch (err) {
      const activeId = err?.response?.data?.activeJobId;
      if (err?.response?.status === 409 && activeId) {
        toast({ title: 'Обновление точек уже выполняется' });
        pollPointsJob(activeId);
      } else {
        toast({
          title: 'Не удалось запустить обновление точек',
          description: err?.response?.data?.message ?? err.message,
          variant: 'destructive'
        });
      }
    }
  }, [queryParams, pollPointsJob]);

  // На монтировании — подхватываем активный job (переживает перезагрузку страницы).
  useEffect(() => {
    if (!iikoEnabled) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const jobs = await listComplaintsPointsRefreshJobs();
        const active = (jobs ?? []).find(isActivePointsJob);
        if (active && !cancelled) {
          setPointsJob(active);
          pollPointsJob(active.id);
        }
      } catch {
        // нет активных задач — игнорируем
      }
    })();
    return () => {
      cancelled = true;
      if (pointsPollRef.current) {
        clearInterval(pointsPollRef.current);
        pointsPollRef.current = null;
      }
    };
  }, [pollPointsJob, iikoEnabled]);

  const pointsActive = isActivePointsJob(pointsJob);

  const openPoint = useCallback((point) => {
    setPointFilter(point);
    setView('list');
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Состав столбцов настраивается в «Интерфейсе»; «Точка» вдобавок требует iiko.
  const caseColumns = visibleCaseColumns({ isVisible: panels.isVisible, iikoEnabled });
  const caseColSpan = caseColumnCount(caseColumns);

  const statCards = [
    {
      panelId: PANEL_IDS['ui:complaints.stat-unique'],
      value: (summary?.uniqueComplaints ?? 0).toLocaleString()
    },
    {
      panelId: PANEL_IDS['ui:complaints.stat-share'],
      value: `${summary?.complaintsSharePercent ?? 0}%`
    },
    {
      panelId: PANEL_IDS['ui:complaints.stat-new'],
      value: (summary?.newComplaints ?? 0).toLocaleString()
    },
    ...(iikoEnabled
      ? [{
        panelId: PANEL_IDS['ui:complaints.stat-top-point'],
        value: summary?.topPoint ? `${summary.topPoint} (${summary.topPointCount})` : '—'
      }]
      : [])
  ].filter((card) => panels.isVisible(card.panelId));

  return (
    <div className="space-y-6">
      <Card data-panel-id={PANEL_IDS['ui:complaints.header']}>
        <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-1.5">
              <CardTitle>{panels.title(PANEL_IDS['ui:complaints.header'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:complaints.header'])} label="О разделе" trackId="complaints.header.hint" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
            {iikoEnabled && (
              <>
                <div className="flex rounded-md border p-0.5">
                  <Button
                    variant={effectiveView === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-1"
                    data-ev="complaints.view.list"
                    onClick={() => setView('list')}
                  >
                    <List className="h-4 w-4" /> Список
                  </Button>
                  <Button
                    variant={effectiveView === 'points' ? 'default' : 'ghost'}
                    size="sm"
                    className="gap-1"
                    data-ev="complaints.view.points"
                    onClick={() => setView('points')}
                  >
                    <MapPin className="h-4 w-4" /> По точкам
                  </Button>
                </div>
                <Button variant="outline" size="sm" className="gap-1" data-ev="complaints.points.refresh" onClick={handleRefreshPoints} disabled={pointsActive} title="Обновить точки из заказов">
                  <RefreshCw className={`h-4 w-4 ${pointsActive ? 'animate-spin' : ''}`} />
                  {pointsActive
                    ? `Обновление точек… ${pointsJob?.processedRows ?? 0}/${pointsJob?.totalRows ?? 0}`
                    : 'Обновить точки'}
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" className="gap-1" data-ev="complaints.export" onClick={handleExport}>
              <FileDown className="h-4 w-4" /> Экспорт
            </Button>
            <Button variant="outline" size="icon" data-ev="complaints.exports.open" onClick={() => setIsPanelOpen(true)} title="Выгрузки">
              <FileSpreadsheet className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <div className="flex flex-1 min-w-[220px] gap-2">
            <Input
              data-ev="complaints.filters.search"
              placeholder="Поиск по телефону"
              value={searchPhone}
              onChange={(event) => setSearchPhone(event.target.value)}
            />
          </div>
          <Select trackId="complaints.filters.status" trackValue value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[170px]"><SelectValue placeholder="Статус" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все статусы</SelectItem>
              {STATUS_ORDER.map((status) => (
                <SelectItem key={status} value={status}>{STATUS_LABELS[status]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showTypeFilter && (
            <Select trackId="complaints.filters.type" value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Тип жалобы" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все типы</SelectItem>
                {typeOptions.map((option) => (
                  <SelectItem key={option} value={option}>{formatNegativeCategory(option)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {showDetailFilter && (
            <Select trackId="complaints.filters.detail" value={detailFilter} onValueChange={setDetailFilter}>
              <SelectTrigger className="w-full sm:w-[180px]"><SelectValue placeholder="Детализация" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Вся детализация</SelectItem>
                {detailOptions.map((option) => (
                  <SelectItem key={option} value={option}>{formatNegativeSubCategory(option)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {iikoEnabled && pointFilter !== 'all' && (
            <Badge variant="outline" className="gap-1 border-sky-200 bg-sky-50 text-sky-800">
              <MapPin className="h-3 w-3" /> {pointFilter}
              <button className="ml-1 text-sky-600" data-ev="complaints.filters.point_clear" onClick={() => setPointFilter('all')}>×</button>
            </Badge>
          )}
          <div className="flex items-center gap-2">
            <Switch id="only-unresolved" data-ev="complaints.filters.only_unresolved" checked={onlyUnresolved} onCheckedChange={setOnlyUnresolved} />
            <label htmlFor="only-unresolved" className="text-sm font-medium">Только неразобранные</label>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="hide-not-complaint" data-ev="complaints.filters.hide_not_complaint" checked={hideNotComplaint} onCheckedChange={setHideNotComplaint} />
            <label htmlFor="hide-not-complaint" className="text-sm font-medium">Скрыть «не жалоба»</label>
          </div>
        </CardContent>
      </Card>

      {pointsActive && (
        <div className="space-y-1 rounded-md border bg-background p-3">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Обновление точек — {pointsJob?.processedRows ?? 0} / {pointsJob?.totalRows ?? 0} телефонов</span>
            <span>{Math.max(0, Math.min(100, Number(pointsJob?.progress ?? 0)))}%</span>
          </div>
          <Progress value={Math.max(0, Math.min(100, Number(pointsJob?.progress ?? 0)))} />
        </div>
      )}

      {/* Подписи плиток приходят из реестра панелей; скрытые отсеиваются до рендера.
          «Точка-лидер» вдобавок зависит от интеграции с iiko. */}
      {statCards.length > 0 && (
        <div className={`grid gap-3 grid-cols-2 ${iikoEnabled ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          {statCards.map((card) => (
            <StatCard key={card.panelId} panelId={card.panelId} title={panels.title(card.panelId)} value={card.value} />
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}

      {/* Два вида одного раздела: список кейсов либо разрез по точкам. Скрывается каждый
          отдельно — панели разные, хоть и показываются по очереди. */}
      {effectiveView === 'list' ? (
        panels.isVisible(PANEL_IDS['ui:complaints.period']) && (
        <Card data-panel-id={PANEL_IDS['ui:complaints.period']}>
          <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <CardTitle>{panels.title(PANEL_IDS['ui:complaints.period'])}</CardTitle>
            <div className="text-sm text-muted-foreground">
              Страница {page} из {totalPages} &middot; Всего {total.toLocaleString()}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {caseColumns.map((panelId) => (
                      <TableHead key={panelId} data-panel-id={panelId} numeric={panelId === PANEL_IDS['ui:complaints.col-calls']}>
                        {panels.title(panelId)}
                      </TableHead>
                    ))}
                    <TableHead className="text-right">Подробнее</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={caseColSpan} className="text-center text-muted-foreground">Загрузка...</TableCell></TableRow>
                  )}
                  {!isLoading && cases.length === 0 && (
                    <TableRow><TableCell colSpan={caseColSpan} className="text-center text-muted-foreground">Нет жалоб за выбранный период.</TableCell></TableRow>
                  )}
                  {cases.map((item) => (
                    <TableRow
                      key={`${item.clientPhone}_${item.complaintDate}`}
                      data-case-key={`${item.clientPhone}_${item.complaintDate}`}
                    >
                      {caseColumns.map((panelId) => (
                        <Fragment key={panelId}>{CASE_CELLS[panelId](item)}</Fragment>
                      ))}
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" data-ev="complaints.table.review" onClick={() => setSelectedCase({ phone: item.clientPhone, date: item.complaintDate })}>
                          <Eye className="h-4 w-4 sm:mr-2" /> <span className="max-sm:sr-only">Разобрать</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
              <span>
                Показано {total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} - {Math.min(page * PAGE_SIZE, total)} из {total}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-ev="complaints.pagination.prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Назад</Button>
                <Button variant="outline" size="sm" data-ev="complaints.pagination.next" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Вперёд</Button>
              </div>
            </div>
          </CardContent>
        </Card>
        )
      ) : (
        panels.isVisible(PANEL_IDS['ui:complaints.by-point']) && (
        <Card data-panel-id={PANEL_IDS['ui:complaints.by-point']}>
          <CardHeader>
            <div className="flex items-center gap-1.5">
              <CardTitle>{panels.title(PANEL_IDS['ui:complaints.by-point'])}</CardTitle>
              <InfoHint text={panels.description(PANEL_IDS['ui:complaints.by-point'])} label="О таблице" trackId="complaints.by_point.hint" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Точка</TableHead>
                    <TableHead numeric>Уникальных жалоб</TableHead>
                    <TableHead numeric>Новых</TableHead>
                    <TableHead numeric>Не жалоба</TableHead>
                    <TableHead>Топ типы</TableHead>
                    <TableHead>Топ детализация</TableHead>
                    <TableHead className="text-right">Жалобы точки</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Загрузка...</TableCell></TableRow>
                  )}
                  {!isLoading && points.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Нет данных по точкам.</TableCell></TableRow>
                  )}
                  {points.map((row) => (
                    <TableRow key={row.point} className="cursor-pointer" data-ev="complaints.points.row.open" onClick={() => openPoint(row.point)}>
                      <TableCell className="font-medium">{row.point}</TableCell>
                      <TableCell numeric>{row.uniqueComplaints ?? 0}</TableCell>
                      <TableCell numeric>{row.newComplaints ?? 0}</TableCell>
                      <TableCell numeric>{row.notComplaintCount ?? 0}</TableCell>
                      <TableCell>{(row.topTypes ?? []).join(', ') || '—'}</TableCell>
                      <TableCell>{(row.topDetails ?? []).join(', ') || '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="gap-1" data-ev="complaints.points.row.open" onClick={(e) => { e.stopPropagation(); openPoint(row.point); }}>
                          Открыть <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        )
      )}

      <ComplaintCaseModal
        caseKey={selectedCase}
        onClose={() => setSelectedCase(null)}
        onSaved={() => { loadCases(); loadSummary(); if (effectiveView === 'points') loadPoints(); }}
      />
    </div>
  );
};

const Complaints = ({ dateRange, callType }) => (
  <ExportJobsProvider api={complaintsExportApi}>
    <ComplaintsInner dateRange={dateRange} callType={callType} />
    <ExportJobsPanel />
  </ExportJobsProvider>
);

export default Complaints;
